import React from "react";
import ReactDOM from "react-dom/client";
import { BrowserRouter } from "react-router-dom";
import "./index.css";
import App from "./App";
import reportWebVitals from "./reportWebVitals";
import {extendTheme} from "@chakra-ui/react";
import {theme} from "@chakra-ui/pro-theme";
import {ChakraProvider, Text, Heading} from "@chakra-ui/react";
import AuthProvider from "./providers/AuthProvider";
import RouteConfigProvider from "./providers/RouteConfigProvider";
import { initializeSupabaseClient } from "./utils/supabaseClient";
import { getTenantContext } from "./utils/tenantConfig";

const proTheme = extendTheme(theme);
const extension = {
    styles: {
        global: {
            html: {
                // Keep native form controls (select, date, etc.) in light mode
                // even when the OS/browser prefers dark mode.
                colorScheme: 'light',
            },
            body: {
                color: '#2D3748', // Neutral gray text color
                bg: '#FFFFFF', // Clean white background
            },
            'select, option, input, textarea': {
                colorScheme: 'light',
            },
        },
    },
    colors: {
        ...proTheme.colors,
        brand: {
            50: '#F7FAFC',
            100: '#EDF2F7',
            200: '#E2E8F0',
            300: '#CBD5E0',
            400: '#A0AEC0',
            500: '#718096',
            600: '#4A5568',
            700: '#2D3748',
            800: '#1A202C',
            900: '#171923',
        },
    },
    fonts: {
        heading: `Inter, sans-serif`,
        body: `Inter, sans-serif`,
    },
    components: {
        Button: {
            baseStyle: {
                fontWeight: 'medium',
                borderRadius: 'md',
            },
            variants: {
                solid: {
                    bg: 'teal.500',
                    color: 'white',
                    _hover: {
                        bg: 'teal.600',
                    },
                    _active: {
                        bg: 'teal.700',
                    },
                },
                outline: {
                    border: '1px solid',
                    borderColor: 'brand.300',
                    color: 'brand.700',
                    _hover: {
                        bg: 'brand.50',
                    },
                },
                ghost: {
                    color: 'brand.700',
                    _hover: {
                        bg: 'brand.50',
                    },
                },
                link: {
                    color: 'brand.600',
                    _hover: {
                        color: 'brand.700',
                        textDecoration: 'underline',
                    },
                },
            },
            defaultProps: {
                variant: 'solid',
            },
        },
        Input: {
            baseStyle: {
                field: {
                    borderColor: 'brand.300',
                    _focus: {
                        borderColor: 'brand.500',
                        boxShadow: '0 0 0 1px var(--chakra-colors-brand-500)',
                    },
                    _hover: {
                        borderColor: 'brand.400',
                    },
                },
            },
        },
        Select: {
            baseStyle: {
                field: {
                    borderColor: 'brand.300',
                    _focus: {
                        borderColor: 'brand.500',
                        boxShadow: '0 0 0 1px var(--chakra-colors-brand-500)',
                    },
                    _hover: {
                        borderColor: 'brand.400',
                    },
                },
            },
        },
        Textarea: {
            baseStyle: {
                borderColor: 'brand.300',
                _focus: {
                    borderColor: 'brand.500',
                    boxShadow: '0 0 0 1px var(--chakra-colors-brand-500)',
                },
                _hover: {
                    borderColor: 'brand.400',
                },
            },
        },
        Link: {
            baseStyle: {
                color: 'brand.600',
                _hover: {
                    color: 'brand.700',
                    textDecoration: 'underline',
                },
            },
        },
        Badge: {
            baseStyle: {
                bg: 'brand.100',
                color: 'brand.700',
            },
        },
        Card: {
            baseStyle: {
                container: {
                    bg: 'white',
                    border: '1px solid',
                    borderColor: 'brand.200',
                    borderRadius: 'lg',
                },
            },
        },
    },
};
const myTheme = extendTheme(extension, proTheme);


const root = ReactDOM.createRoot(document.getElementById("root"));

class AppErrorBoundary extends React.Component {
  state = { hasError: false, error: null };

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error("AppErrorBoundary caught:", error, errorInfo);
  }

  render() {
    if (this.state.hasError && this.state.error) {
      return (
        <div style={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: 24,
          background: "#f4eeea",
          color: "#0e1735",
          fontFamily: "system-ui, sans-serif",
        }}>
          <div style={{ maxWidth: 640 }}>
            <h2 style={{ marginBottom: 16 }}>Something went wrong</h2>
            <pre style={{
              padding: 16,
              background: "#fff",
              borderRadius: 8,
              overflow: "auto",
              fontSize: 14,
            }}>
              {this.state.error?.message || String(this.state.error)}
            </pre>
            <p style={{ marginTop: 16, color: "#666" }}>
              Check the browser console for more details.
            </p>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

const TenantBootstrapError = ({ error }) => {
    const { hostname, tenantId } = getTenantContext();
    return (
        <div style={{
            minHeight: "100vh",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 24,
            background: "#f4eeea",
            color: "#0e1735",
            fontFamily: "system-ui, sans-serif",
        }}>
            <div style={{ maxWidth: 640, textAlign: "center" }}>
                <Heading size="md" mb={4}>Tenant configuration error</Heading>
                <Text mb={3} color="#0e1735">
                    Could not initialize this tenant.
                </Text>
                <Text mb={3} color="#0e1735">
                    Host: <strong>{hostname || "unknown"}</strong> | Tenant: <strong>{tenantId || "unknown"}</strong>
                </Text>
                <Text color="#4a5568">
                    {error?.message || "Please check tenant mapping in your environment and try again."}
                </Text>
            </div>
        </div>
    );
};

const renderRoot = (bootstrapError = null) => {
    root.render(
        <React.StrictMode>
            <ChakraProvider theme={myTheme}>
                {bootstrapError ? (
                    <TenantBootstrapError error={bootstrapError} />
                ) : (
                    <AppErrorBoundary>
                        <AuthProvider>
                            <BrowserRouter>
                                <RouteConfigProvider>
                                    <App/>
                                </RouteConfigProvider>
                            </BrowserRouter>
                        </AuthProvider>
                    </AppErrorBoundary>
                )}
            </ChakraProvider>
        </React.StrictMode>
    );
};

const bootstrap = async () => {
    try {
        await initializeSupabaseClient();
        renderRoot();
    } catch (error) {
        console.error("[bootstrap] Failed to initialize tenant Supabase client:", error);
        renderRoot(error);
    }
};

bootstrap();

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
